﻿<template>
  <!-- The device EDITOR (name, room, icon, thresholds, delete/duplicate) is the
       app's settings surface for one device: a viewer never gets it, whichever
       host opened it (card t_803e86a4). The hub guard is the security layer;
       this is the door. -->
  <div
    v-if="device && !isViewer"
    class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    @mousedown.self="$emit('close')">
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
      <!-- Modal Header -->
      <div class="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div class="flex items-center gap-3">
          <span class="material-symbols-outlined text-3xl leading-none">{{ device.icon || 'devices' }}</span>
          <div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{{ device.deviceName }}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 uppercase">
              {{ device.deviceType || tt('Device', 'Naprava') }}
            </p>
          </div>
        </div>
        <button @click="$emit('close')" class="text-gray-400 hover:text-gray-500">
          <span class="material-symbols-outlined">close</span>
        </button>
      </div>

      <!-- Device Editor Component -->
      <DeviceEditor ref="deviceEditorRef" mode="edit" :device="device" class="flex-1 overflow-hidden" />

      <!-- Modal Footer -->
      <div class="flex justify-between items-center px-6 py-4 border-t border-gray-200 dark:border-gray-700">
        <div class="flex gap-3">
          <button
            v-if="canDuplicate"
            @click="handleDuplicate"
            :disabled="duplicating || deleting || saving"
            class="px-4 py-2 border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50 disabled:cursor-not-allowed">
            {{ duplicating ? tt('Duplicating...', 'Podvajanje...') : tt('Duplicate Device', 'Podvoji napravo') }}
          </button>
          <button
            @click="handleDelete"
            :disabled="deleting || saving || duplicating"
            class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed">
            {{ deleting ? tt('Deleting...', 'Brisanje...') : tt('Delete Device', 'Izbriši napravo') }}
          </button>
        </div>
        <div class="flex gap-3">
          <button
            @click="$emit('close')"
            :disabled="saving || deleting || duplicating"
            class="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">
            {{ tt('Cancel', 'Prekliči') }}
          </button>
          <button
            @click="handleSave"
            :disabled="saving || deleting || duplicating"
            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400">
            {{ saving ? tt('Saving...', 'Shranjevanje...') : tt('Save Changes', 'Shrani spremembe') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { apiFetch, DEVICE_DELETE_TIMEOUT_MS } from '../services/api';
import DeviceEditor from './DeviceEditor.vue';
import type { Device } from '@/types';
import { useI18n } from 'vue-i18n';
import { useToast } from '../composables/useToast';
import { useMessageBox } from '../composables/useMessageBox';
import { useDemoGuest } from '../composables/useDemoGuest';
import { tt } from '../utils/consoleText';

const { t } = useI18n();
const toast = useToast();
const { confirm: confirmBox } = useMessageBox();
/** See the template comment: the editor is not offered to a viewer (card t_803e86a4). */
const { isViewer } = useDemoGuest();

const props = defineProps<{
  device: Device;
  /**
   * Integration type of the device's integration (e.g. 'knx'). The hub's
   * duplicate endpoint supports KNX only, so duplication is gated on this
   * rather than on a per-protocol classId (device classes are canonical).
   */
  integrationType?: string;
}>();

const emit = defineEmits<{
  close: [];
  refresh: [updatedDevice?: Device];
  deleted: [];
}>();

const deviceEditorRef = ref<InstanceType<typeof DeviceEditor> | null>(null);
const saving = ref(false);
const deleting = ref(false);
const duplicating = ref(false);
const canDuplicate = computed(() => props.integrationType?.toLowerCase() === 'knx');

async function handleDuplicate() {
  try {
    duplicating.value = true;
    const duplicatedDevice = await apiFetch(`/devices/${props.device.id}/duplicate`, {
      method: 'POST',
    });
    emit('refresh', duplicatedDevice);
    emit('close');
  } catch (error) {
    console.error('Failed to duplicate device:', error);
    toast.error(
      t('dialogs.deviceDuplicateFailed', {
        message: error instanceof Error ? error.message : tt('Unknown error', 'Neznana napaka'),
      }),
    );
  } finally {
    duplicating.value = false;
  }
}

async function handleSave() {
  // Regular device save path
  if (!deviceEditorRef.value) return;

  const validation = deviceEditorRef.value.validate();
  if (!validation.valid) {
    toast.error(t('dialogs.validationErrors', { errors: validation.errors.join('\n') }));
    return;
  }

  try {
    saving.value = true;
    const updates = deviceEditorRef.value.getData();

    console.log('[DeviceSettingsModalNew] updates from getData():', updates);
    console.log('[DeviceSettingsModalNew] updates.settings:', updates.settings);

    const patchBody = {
      deviceName: updates.deviceName,
      shortName: updates.shortName,
      speechFullName: updates.speechFullName,
      speechShortName: updates.speechShortName,
      brandName: updates.brandName,
      productName: updates.productName,
      modelNumber: updates.modelNumber,
      manualUrl: updates.manualUrl,
      productUrl: updates.productUrl,
      roomId: updates.roomId,
      icon: updates.icon,
      enabled: updates.enabled,
      settings: updates.settings,
      logicScript: updates.logicScript,
    };

    console.log('[DeviceSettingsModalNew] PATCH body:', patchBody);

    // Update basic device fields
    const updatedDevice = await apiFetch(`/devices/${props.device.id}`, {
      method: 'PATCH',
      body: JSON.stringify(patchBody),
    });

    // Update references
    const referencesUpdated = await deviceEditorRef.value.saveReferences();
    if (!referencesUpdated) {
      return; // Errors already shown by saveReferences
    }

    // Emit updated device so parent can update its local state immediately
    emit('refresh', updatedDevice);
    emit('close');
  } catch (error) {
    console.error('Failed to save device:', error);
    toast.error(t('dialogs.saveFailed'));
  } finally {
    saving.value = false;
  }
}

async function handleDelete() {
  const ok = await confirmBox(t('dialogs.deviceDeleteConfirm', { name: props.device.deviceName }));
  if (!ok) {
    return;
  }

  try {
    deleting.value = true;
    await apiFetch(`/devices/${props.device.id}`, {
      method: 'DELETE',
      timeoutMs: DEVICE_DELETE_TIMEOUT_MS,
    });

    // Emit deleted event and close modal
    emit('deleted');
    emit('close');
  } catch (error) {
    console.error('Failed to delete device:', error);
    toast.error(
      t('dialogs.deviceDeleteFailedMessage', {
        message: error instanceof Error ? error.message : tt('Unknown error', 'Neznana napaka'),
      }),
    );
  } finally {
    deleting.value = false;
  }
}
</script>
